import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { refreshApiView } from '@e3dc-react/shell/State/ApiView/ApiViewActions'
import { useDispatch } from 'react-redux'
import { CustomLayout, CustomTrace } from 'src/Libs/chart-kit/shared/utils/chartModel'
import { useTypedSelector } from 'src/State/RootReducer'
import { useEffect } from 'react'
import { ChartSettings } from '../components'
import { getChartsEditingSettings } from '../utils'
import { useUserSettings } from 'src/Hooks'

interface DeleteChartProps {
  chartId: string | number
  userId?: number
  chartSettingsList: ChartSettings[]
  installationId: number
  storageSerialNumber: string
}

export interface SaveChartProps {
  chartId?: string | number
  chartSettings: ChartSettings
  userId?: number
  chartSettingsList: ChartSettings[]
  installationId?: number
  storageSerialNumber?: string
  isInitial: boolean
  showSuccessMessage?: boolean
}

/**
 * This hook is used to update the chart config stored in the database (user settings).
 * The chart config stores custom charts and default charts where the user changed the preset config.
 *
 * @param userSettings includes chart config for new/updated charts
 * @param defaultAndCustomCharts all available charts to the user
 * @returns
 */
export const useUpdateEditedChartSettings = (
  defaultAndCustomCharts?: CustomLayout[]
): {
  deleteChart: (props: DeleteChartProps) => Promise<void>
  saveCharts: (props: SaveChartProps) => Promise<void>
} => {
  const dispatch = useDispatch()
  const { enqueueErrorStack, enqueueSuccessStack } = useSnackbar()
  const { t } = useCustomTranslation()
  const installationId = useTypedSelector(state => state.storage.activeInstallation)
  const storageId = useTypedSelector(state => state.storage.activeStorage) ?? ''
  const userId = useTypedSelector(state => state.auth.userId)
  const { userSettings, updateUserSettings } = useUserSettings()
  const chartSettingsList = getChartsEditingSettings(userSettings)

  const updatedChartSettings: ChartSettings[] = checkAndUpdateChartSettingTraces(chartSettingsList, storageId, installationId, defaultAndCustomCharts)

  useEffect(() => {
    if (updatedChartSettings.length > 0) {
      updatedChartSettings.forEach(chartSetting => {
        saveCharts({
          chartId: chartSetting.id,
          chartSettings: chartSetting,
          userId,
          chartSettingsList,
          installationId,
          storageSerialNumber: storageId,
          isInitial: false,
          showSuccessMessage: false,
        })
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, storageId, installationId, updatedChartSettings])

  const saveCharts = async ({
    chartId,
    chartSettings,
    userId,
    chartSettingsList,
    installationId,
    storageSerialNumber,
    isInitial,
    showSuccessMessage = true,
  }: SaveChartProps): Promise<void> => {
    const filterChart = (chart: ChartSettings): boolean =>
      chart.id?.toString() !== chartId?.toString() || chart.installationId !== installationId || chart.storageSerialNumber !== storageSerialNumber

    const updatedChartId = Number(chartId) || chartId

    if (userId) {
      try {
        await updateUserSettings({
          chartConfig: [
            ...chartSettingsList.filter(chart => filterChart(chart)).map(chart => JSON.stringify(chart)),
            JSON.stringify({ ...chartSettings, id: updatedChartId, installationId, storageSerialNumber, isInitial }),
          ],
        })
        if (showSuccessMessage) enqueueSuccessStack(`${t('systemDataHandler.editor.saveChartSucceded')}`)
      } catch (err) {
        enqueueErrorStack(`${t('systemDataHandler.editor.saveChartFailed')}: ${err}`)
      } finally {
        dispatch(refreshApiView())
      }
    }
  }

  /**
   * Delete charts if: (1) they have the parameter chartId (2) do not belong to the installation
   */
  const deleteChart = async ({ chartId, userId, chartSettingsList, installationId, storageSerialNumber }: DeleteChartProps): Promise<void> => {
    const filterChart = (chart: ChartSettings): boolean =>
      !(chart.id?.toString() === chartId?.toString() && chart.installationId === installationId && chart.storageSerialNumber === storageSerialNumber)

    if (userId) {
      try {
        await updateUserSettings({
          chartConfig: [...chartSettingsList.filter(chart => filterChart(chart)).map(chart => JSON.stringify(chart))],
        })
        enqueueSuccessStack(`${t('systemDataHandler.editor.deleteChartSucceded')}`)
      } catch (err) {
        enqueueErrorStack(`${t('systemDataHandler.editor.deleteChartFailed')}: ${err}`)
      } finally {
        dispatch(refreshApiView())
      }
    }
  }

  return { deleteChart, saveCharts }
}

/**
 * Compares the charts in the settings (chartSettings) and the available charts (defaultAndCustomCharts) and adds the traces that are not yet stored in the settings.
 * This can happen if e.g. the user adds a new device (wallbox, powermeter) to the installation.
 *
 * @param chartSettings chart config stored in the database for the user
 * @param defaultAndCustomCharts charts available to the user
 * @param storageId
 * @returns
 */
const checkAndUpdateChartSettingTraces = (
  chartSettings: ChartSettings[],
  storageId: string,
  installationId: number | undefined,
  defaultAndCustomCharts?: CustomLayout[]
): ChartSettings[] => {
  const updatedChartSettings: ChartSettings[] = chartSettings?.reduce((acc, chartSetting) => {
    const newTraces = findNewTraces(chartSetting, storageId, installationId, defaultAndCustomCharts)
    if (newTraces.length > 0) {
      acc.push({ ...chartSetting, traces: [...chartSetting.traces, ...newTraces] })
    }
    return acc
  }, [] as ChartSettings[])

  return updatedChartSettings
}

/**
 * Finds the traces that are not yet stored in the settings but available for a chart (e.g. because new wallbox is added)
 */
const findNewTraces = (
  chartSetting: ChartSettings,
  storageId: string,
  installationId: number | undefined,
  defaultAndCustomCharts?: CustomLayout[]
): CustomTrace[] => {
  const chart = defaultAndCustomCharts?.find(chart => chart.isInitial && chart.id?.toString() === chartSetting.id)
  const newTraces = chart?.traces.filter(
    trace =>
      !chartSetting.traces.some(settingTrace => settingTrace.key === trace.key) &&
      chartSetting.storageSerialNumber === storageId &&
      chartSetting.installationId === installationId
  )
  return newTraces ?? []
}
